import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { CloudSync as SaveIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

interface CountryConfigData {
  iva_percentage: number;
  flete_enabled: boolean;
}

// Configuración por país: IVA % (aplica SOLO a Materiales, CLAUDE.md §6) y si
// la sección Flete está habilitada. Los endpoints ya existían
// (GET/PUT /settings/config/country) pero no había UI, por lo que el IVA
// quedaba en su default y los totales se veían mal. Pantalla mínima a
// propósito (CLAUDE.md §6 / decisión del PO): sólo IVA + Flete on/off; la
// config de campos fijos del ERP queda para el trabajo de Phase 7.
// Modelada sobre ConditionsConfig.tsx (el patrón existente más cercano).
export const CountryConfig: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [config, setConfig] = useState<CountryConfigData>({
    iva_percentage: 0,
    flete_enabled: true,
  });
  const [ivaInput, setIvaInput] = useState<string>('0');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.get<CountryConfigData>('/settings/config/country');
      setConfig(data);
      setIvaInput(String(data.iva_percentage ?? 0));
      setSaved(false);
    } catch (err) {
      console.error('Error loading country config:', err);
      setError('Error al cargar la configuración del país');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConfig = async () => {
    const ivaValue = Number(ivaInput);
    if (Number.isNaN(ivaValue) || ivaValue < 0 || ivaValue > 100) {
      setError('El IVA debe ser un número entre 0 y 100.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const updated = await apiService.put<CountryConfigData>('/settings/config/country', {
        iva_percentage: ivaValue,
        flete_enabled: config.flete_enabled,
      });
      setConfig(updated);
      setIvaInput(String(updated.iva_percentage ?? ivaValue));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving country config:', err);
      setError('Error al guardar la configuración del país');
    } finally {
      setLoading(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tenés permisos para acceder a esta sección. Solo los administradores pueden editar la configuración del país.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Configuración del país
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Parámetros generales aplicados a todos los presupuestos de este país.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {saved && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ✓ Configuración guardada exitosamente
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={3}>
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                IVA (%)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Se aplica únicamente a la sección Materiales. Mano de Obra y Flete no llevan IVA.
              </Typography>
              <TextField
                type="number"
                value={ivaInput}
                onChange={(e) => setIvaInput(e.target.value)}
                disabled={loading}
                variant="outlined"
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ maxWidth: 220 }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Flete
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.flete_enabled}
                    onChange={(e) => setConfig({ ...config, flete_enabled: e.target.checked })}
                    disabled={loading}
                  />
                }
                label={config.flete_enabled ? 'Sección Flete habilitada' : 'Sección Flete deshabilitada'}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={saveConfig}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
              <Button variant="outlined" onClick={loadConfig} disabled={loading}>
                Recargar
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info">
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          💡 Cómo se usan estos parámetros:
        </Typography>
        <Typography variant="body2">
          El IVA se suma al total de Materiales que ve el cliente (una sola línea "Total + IVA").
          Si Flete está deshabilitado, la sección Flete no se ofrece en los presupuestos de este país.
        </Typography>
      </Alert>
    </Box>
  );
};

export default CountryConfig;
